import { EvoluaApiError } from "@evolua/api-client";
import type { MeRequestKind } from "@evolua/api-client";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  PageHeader,
  Select,
  Skeleton,
  Table,
  Textarea,
  StatusBadge,
  buttonClassName,
} from "@evolua/ui";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  useAddMeRequestMessage,
  useCreateMeRequest,
  useMeRequest,
  useMeRequests,
} from "../api/use-me-requests.js";

const KIND_LABELS: Record<MeRequestKind, string> = {
  DECLARATION: "Declaração",
  DATA_CORRECTION: "Correção de dado",
  QUESTION: "Dúvida",
};

function DetailPanel({ id }: { id: string }) {
  const query = useMeRequest(id);
  const addMessage = useAddMeRequestMessage();
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (query.isPending) return <Skeleton height="8rem" label="Detalhe" />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        description="Não foi possível carregar a solicitação."
        onRetry={() => void query.refetch()}
      />
    );
  }

  const detail = query.data;

  async function onSend() {
    if (!body.trim()) return;
    setError(null);
    try {
      await addMessage.mutateAsync({ id, body: { body: body.trim() } });
      setBody("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao enviar");
    }
  }

  return (
    <div className="evolua-stack evolua-stack--3">
      <div>
        <Link to="/minhas-solicitacoes">← Voltar</Link>
      </div>
      <Card>
        <div className="evolua-stack evolua-stack--3">
          <Badge tone="neutral">
            {KIND_LABELS[detail.kind as MeRequestKind] ?? detail.kind}
          </Badge>
          <StatusBadge status={detail.status} />
        </div>
        <h2 className="evolua-section-title">{detail.subject}</h2>
        <p>{detail.body}</p>
        {detail.attachmentObjectIds.length > 0 ? (
          <p className="evolua-muted">
            Anexos (object ids): {detail.attachmentObjectIds.join(", ")}
          </p>
        ) : null}
      </Card>

      <section aria-labelledby="thread-title">
        <h3 id="thread-title">Mensagens</h3>
        <ul className="evolua-stack evolua-stack--3">
          {detail.messages.map((m) => (
            <li key={m.id}>
              <Card>
                <strong>{m.fromRole === "HR" ? "RH" : "Você"}</strong>
                <span className="evolua-muted">
                  {new Date(m.at).toLocaleString("pt-BR")}
                </span>
                <p>{m.body}</p>
              </Card>
            </li>
          ))}
        </ul>
      </section>

      {detail.status === "IN_PROGRESS" ? (
        <div className="evolua-stack evolua-stack--2">
          <Textarea
            id="msg-body"
            label="Nova mensagem"
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <Button
            type="button"
            size="sm"
            disabled={addMessage.isPending || !body.trim()}
            onClick={() => void onSend()}
          >
            Enviar
          </Button>
          {error ? <Alert tone="danger">{error}</Alert> : null}
        </div>
      ) : null}

      <section>
        <h3>Histórico</h3>
        <Table
          caption="Histórico"
          getRowId={(h) => h.id}
          columns={[
            { id: "action", header: "Ação", cell: (h) => h.action },
            {
              id: "at",
              header: "Quando",
              cell: (h) => new Date(h.at).toLocaleString("pt-BR"),
            },
            { id: "comment", header: "Comentário", cell: (h) => h.comment ?? "—" },
          ]}
          rows={detail.history}
        />
      </section>
    </div>
  );
}

/** Portal do colaborador — solicitações genéricas ao RH (FASE-06-T39). */
export function MinhasSolicitacoesPage() {
  const { requestId } = useParams();
  const list = useMeRequests();
  const create = useCreateMeRequest();

  const [kind, setKind] = useState<MeRequestKind>("QUESTION");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachmentId, setAttachmentId] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  if (requestId) {
    return (
      <>
        <PageHeader title="Solicitação" />
        <DetailPanel id={requestId} />
      </>
    );
  }

  const correlationId =
    list.error instanceof EvoluaApiError ? list.error.problem.traceId : undefined;

  async function onCreate() {
    setFormError(null);
    if (subject.trim().length < 3 || body.trim().length < 3) {
      setFormError("Informe assunto e mensagem (mín. 3 caracteres).");
      return;
    }
    try {
      await create.mutateAsync({
        kind,
        subject: subject.trim(),
        body: body.trim(),
        ...(attachmentId.trim() ? { attachmentObjectIds: [attachmentId.trim()] } : {}),
      });
      setSubject("");
      setBody("");
      setAttachmentId("");
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Falha ao criar");
    }
  }

  return (
    <>
      <PageHeader
        title="Minhas solicitações"
        lead="Canal rastreável com o RH: declaração, correção de dado ou dúvida — com mensagens e anexos."
      />

      <Card title="Nova solicitação">
        <form
          className="evolua-stack evolua-stack--3"
          onSubmit={(e) => {
            e.preventDefault();
            void onCreate();
          }}
        >
          <Select
            label="Tipo"
            value={kind}
            onChange={(e) => setKind(e.target.value as MeRequestKind)}
            options={(Object.keys(KIND_LABELS) as MeRequestKind[]).map((k) => ({
              value: k,
              label: KIND_LABELS[k],
            }))}
          />
          <Field
            label="Assunto"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={120}
          />
          <Textarea
            label="Mensagem"
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <Field
            label="Anexo (UUID mock de storage)"
            value={attachmentId}
            onChange={(e) => setAttachmentId(e.target.value)}
            placeholder="opcional"
          />
          <Button type="submit" size="sm" disabled={create.isPending}>
            {create.isPending ? "Enviando…" : "Abrir solicitação"}
          </Button>
          {formError ? <Alert tone="danger">{formError}</Alert> : null}
        </form>
      </Card>

      {list.isPending ? (
        <Skeleton height="4rem" />
      ) : list.isError ? (
        <ErrorState
          {...(correlationId ? { correlationId } : {})}
          onRetry={() => void list.refetch()}
        />
      ) : list.data.data.length === 0 ? (
        <EmptyState
          title="Nenhuma solicitação"
          description="Abra a primeira solicitação acima."
        />
      ) : (
        <Table
          caption="Minhas solicitações"
          getRowId={(item) => item.id}
          columns={[
            {
              id: "kind",
              header: "Tipo",
              cell: (item) => (
                <Badge tone="neutral">
                  {KIND_LABELS[item.kind as MeRequestKind] ?? item.kind}
                </Badge>
              ),
            },
            { id: "subject", header: "Assunto", cell: (item) => item.subject },
            {
              id: "status",
              header: "Status",
              cell: (item) => <StatusBadge status={item.status} />,
            },
            {
              id: "created",
              header: "Aberta em",
              cell: (item) => new Date(item.createdAt).toLocaleString("pt-BR"),
            },
            {
              id: "actions",
              header: "Ações",
              cell: (item) => (
                <Link
                  to={`/minhas-solicitacoes/${item.id}`}
                  className={buttonClassName({ variant: "secondary", size: "sm" })}
                >
                  Abrir
                </Link>
              ),
            },
          ]}
          rows={list.data.data}
        />
      )}

      <p className="evolua-mt">
        <Badge tone="neutral">Mock-first</Badge> Anexos = object ids de storage (FASE
        storage real).
      </p>
    </>
  );
}
